import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, DatePicker, Input, Row, Tooltip, Form } from "antd";
import moment from "moment";
import { v4 as uuid } from "uuid";
import { ReactComponent as CancelButtonIcon } from "../../img/buttons_icons/cancelButton_icon.svg";
import { ReactComponent as SubmitButtonIcon } from "../../img/buttons_icons/submitButton_icon.svg";
import { ReactComponent as CloseVersionIcon } from "../../img/versions_icons/closeVersion_icon.svg";
import { ReactComponent as AddVersionIcon } from "../../img/versions_icons/addVersion_icon.svg";
import { displayFormat, toDisplayFormat, toHttpFormatStartOfDay } from "../../lib/utils/formatDate";
import "../../styles/row.less";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_ACCOUNTINFO_WRITE } from "../../lib/const/permissions";
import ToggleableVersionsTable from "../ToggleableVersionsTable/ToggleableVersionsTable";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import rtkRequestStatusCodes from "../../lib/const/rtkRequestStatusCodes";

export default function AccountExiteIdVersionsTable(props) {
  const {
    fetchSubscriberVersions,
    handleCreateSubscriberVersion,
    handleCloseSubscriberVersion,
    handleCreateSubscriberVersionLoading,
  } = props;
  const { account, accountSubscribers, accountSubscribersLoading } = useSelector(
    (state) => state.accounts
  );

  const { subscribersVersions, subscribersVersionsLoading } = useSelector(
    (state) => state.accountsTs.accountListRowData
  );

  const currentDate = moment();
  const [currentVersion, setCurrentVersion] = useState();
  const [childTableData, setChildTableData] = useState([]);
  const [tableData, setTableData] = useState([]);

  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);

  const [form] = Form.useForm();

  const handleSetInitialValue = (field) => {
    setTimeout(() => {
      form.setFieldsValue(field);
    }, 0);
  };

  const [currentRec, setCurrentRec] = useState({});

  useEffect(() => {
    if (checkIsLoaded(accountSubscribersLoading)) {
      setTableData(
        accountSubscribers
          .map((item) => ({
            key: item.id,
            id: item.id,
            externalId: item.externalId,
            fromDate: item.fromDate,
            toDate: item.toDate,
          }))
          .sort((a, b) => {
            if (a.fromDate > b.fromDate) {
              return -1;
            }
            if (a.fromDate < b.fromDate) {
              return 1;
            }
            return 0;
          })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountSubscribersLoading]);

  useEffect(() => {
    if (checkIsLoaded(subscribersVersionsLoading)) {
      setChildTableData(
        subscribersVersions[0]
          ?.map((version, i) => ({
            key: `${version.id}${version.fromDate}`,
            id: version.id,
            externalId: version.externalId,
            subscriberTypeId: version.subscriberTypeId,
            fromDate: version.fromDate,
            toDate: version.toDate,
            isCurrentVersion:
              moment(currentDate).isSameOrAfter(version.fromDate) &&
              moment(currentDate).isBefore(version.toDate),
            isPastVersion: moment(version?.toDate).isBefore(currentDate),
            isLatestVersion: subscribersVersions[0].length === i + 1,
            isClosing: false,
            isCreated: false,
          }))
          .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribersVersionsLoading]);

  const handleCreateVersion = ({ fromDate, externalId }) => {
    const createdVersion = childTableData.find((item) => item.isCreated);
    [
      "key",
      "isClosing",
      "isCreated",
      "isCurrentVersion",
      "isLatestVersion",
      "isPastVersion",
      "fromDate",
      "toDate",
    ].forEach((i) => delete createdVersion[i]);
    handleCreateSubscriberVersion(
      createdVersion.id,
      toHttpFormatStartOfDay(fromDate),
      {
        ...createdVersion,
        externalId,
        accountId: account.id,
      }
      // ,
      // true
    );
  };

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    externalId: "",
  };

  const [columns, setColumns] = useState([
    {
      title: "Технический ID",
      dataIndex: "externalId",
      key: "externalId",
      width: columnsWidths.externalId,
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      render: (text, record) =>
        `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`,
    },
    {
      title: "",
      dataIndex: "actions",
      width: columnsWidths.actions,
      key: "actions",
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Технический ID",
      dataIndex: "externalId",
      key: "externalId",
      width: columnsWidths.externalId,
      hidden: false,
      render(text, record) {
        if (record.isCreated) {
          return (
            <Form.Item
              name="externalId"
              className="form-item-required__hidden-error__mb0"
              rules={[{ required: true }]}
            >
              <Input />
            </Form.Item>
          );
        }
        return record.externalId;
      },
    },
    {
      title: "Период действия",
      key: "period",
      dataIndex: "period",
      width: columnsWidths.period,
      hidden: false,
      render(text, record) {
        if (record.isCreated) {
          const nextFromDate = moment(record.fromDate).add(1, "day");
          const allowedFromDate = moment().startOf("month").startOf("day");
          handleSetInitialValue({ fromDate: moment.max(nextFromDate, allowedFromDate) });
          return (
            <>
              <Form.Item
                name="fromDate"
                className="form-item-required__hidden-error__mb0"
                rules={[{ required: true }]}
                style={{ display: "inline-block", marginRight: "5px" }}
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) =>
                    current.isSameOrBefore(record.fromDate, "day") ||
                    current.isAfter(record.toDate, "day") ||
                    current.isBefore(moment().startOf("month"), "day")
                  }
                />
              </Form.Item>
              <Form.Item
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
              >
                -{toDisplayFormat(record.toDate)}
              </Form.Item>
            </>
          );
        }
        if (record.isClosing) {
          handleSetInitialValue({ toDate: moment(record.toDate) });
          return (
            <>
              <Form.Item
                className="form-item-required__hidden-error__mb0"
                style={{ display: "inline-block", marginRight: "5px" }}
              >
                {toDisplayFormat(record.fromDate)} -
              </Form.Item>
              <Form.Item
                name="toDate"
                rules={[{ required: true }]}
                style={{ display: "inline-block" }}
                className="form-item-required__hidden-error__mb0"
              >
                <DatePicker
                  format={displayFormat}
                  allowClear={false}
                  disabledDate={(current) =>
                    current.isBefore(record.fromDate, "day") ||
                    current.isAfter(record.toDate, "day") ||
                    current.isBefore(moment().startOf("month"), "day")
                  }
                />
              </Form.Item>
            </>
          );
        }
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        if (hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE)) {
          if (!record.isPastVersion && !record.isCreated && !record.isClosing) {
            const addVersion = (e) => {
              e.stopPropagation();
              const newVersion = {
                ...record,
                key: uuid(),
                isCreated: true,
                isCurrentVersion: false,
                isPastVersion: false,
                isLatestVersion: false,
              };
              setCurrentRec(newVersion);
              setChildTableData((prevState) =>
                [newVersion, ...prevState].sort(versionsTableUtils.sortVersionsByDate)
              );
              handleSetInitialValue({ externalId: record.externalId });
            };

            const changeToDateVersion = (e) => {
              e.stopPropagation();
              setChildTableData((prevState) =>
                prevState.map((item) => {
                  if (item.key === record.key) {
                    const updatedRecord = {
                      ...item,
                      isClosing: true,
                    };
                    setCurrentRec(updatedRecord);
                    return updatedRecord;
                  }
                  return item;
                })
              );
            };

            return (
              <Row>
                <Tooltip title="Добавить версию">
                  <Button
                    className="button-icon"
                    icon={<AddVersionIcon />}
                    type="text"
                    htmlType="button"
                    disabled={isChangingForbidden}
                    onClick={addVersion}
                  />
                </Tooltip>
                {record.isLatestVersion && (
                  <Tooltip title="Изменить дату закрытия">
                    <Button
                      className="button-icon"
                      icon={<CloseVersionIcon />}
                      type="text"
                      htmlType="button"
                      disabled={isChangingForbidden}
                      onClick={changeToDateVersion}
                    />
                  </Tooltip>
                )}
              </Row>
            );
          }
          if (record.isCreated || record.isClosing) {
            const handleDiscard = (e) => {
              e.stopPropagation();
              setChildTableData((prevState) =>
                prevState
                  .map((item) => {
                    if (item.isClosing) {
                      return {
                        ...item,
                        isClosing: false,
                      };
                    }
                    if (item.isCreated) {
                      return null;
                    }
                    return item;
                  })
                  .filter((obj) => obj !== null)
              );
              form.resetFields();
              setCurrentRec({});
            };

            return (
              <Row>
                <Tooltip title="Подтвердить">
                  <Button
                    className="button-icon"
                    icon={<SubmitButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      form.submit();
                    }}
                  />
                </Tooltip>
                <Tooltip title="Отменить">
                  <Button
                    className="button-icon"
                    icon={<CancelButtonIcon />}
                    type="text"
                    htmlType="button"
                    onClick={handleDiscard}
                  />
                </Tooltip>
              </Row>
            );
          }
        }
        return null;
      },
    },
    {
      title: "",
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  // если ошибка при создании версии у ExiteId, то закрывать форму создания новой версии
  useEffect(() => {
    if (handleCreateSubscriberVersionLoading === rtkRequestStatusCodes.rejected) {
      setChildTableData((prevState) => prevState.slice(1, prevState.length));
    }
  }, [handleCreateSubscriberVersionLoading]);

  const handleSubmit = (values) => {
    if (currentRec.isCreated) {
      handleCreateVersion(values);
    } else if (currentRec.isClosing) {
      handleCloseSubscriberVersion(currentRec.id, values.toDate);
    }
    setCurrentRec({});
  };

  return (
    <Form form={form} onFinish={handleSubmit}>
      <ToggleableVersionsTable
        tableName="organization-exiteid-table"
        columns={columns}
        setColumns={setColumns}
        childTableColumns={childColumns}
        setChildTableColumns={setChildColumns}
        tableData={tableData}
        childTableData={childTableData}
        isChildTableLoading={checkIsLoading(subscribersVersionsLoading)}
        tableClassName="organization-exiteid-table"
        isPageable={false}
        isToggleableColumns={false}
        onOpenVersionsCallback={(record) => {
          setCurrentVersion(record);
          fetchSubscriberVersions([record.id]);
        }}
      />
    </Form>
  );
}
